import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Language = 'ru' | 'uz';

interface LanguageState {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

// Translations
const translations: Record<Language, Record<string, string>> = {
  ru: {
    // App name
    'app.name': 'My Helper',
    'app.subtitle': 'CRM Система',

    // Auth
    'auth.title': 'Вход в систему',
    'auth.login': 'Логин',
    'auth.password': 'Пароль',
    'auth.enter': 'Войти',
    'auth.error': 'Неверный логин или пароль',
    'auth.logout': 'Выйти',

    // Navigation
    'nav.dashboard': 'Дашборд',
    'nav.requests': 'Заявки',
    'nav.executors': 'Исполнители',
    'nav.residents': 'Жители',
    'nav.buildings': 'Здания',
    'nav.activity': 'Журнал действий',
    'nav.reports': 'Отчёты',
    'nav.settings': 'Настройки',
    'nav.monitoring': 'Мониторинг',
    'nav.myRequests': 'Мои заявки',
    'nav.services': 'Услуги',
    'nav.profile': 'Профиль',
    'nav.more': 'Ещё',
    'nav.rentals': 'Аренда квартир',
    'nav.myApartments': 'Мои квартиры',
    'nav.accounts': 'Лицевые счета',
    'nav.owners': 'Собственники',
    'nav.workOrders': 'Наряды на работы',
    'nav.trainings': 'Тренинги',

    // Dashboard
    'dashboard.manager': 'Панель менеджера',
    'dashboard.admin': 'Панель администратора',
    'dashboard.newRequests': 'Новые заявки',
    'dashboard.inProgress': 'В работе',
    'dashboard.pendingApproval': 'Ожидают подтверждения',
    'dashboard.completedWeek': 'Выполнено за неделю',
    'dashboard.weeklyRequests': 'Заявки за неделю',
    'dashboard.byCategory': 'По категориям',
    'dashboard.recentRequests': 'Последние заявки',
    'dashboard.viewAll': 'Все',

    // Requests
    'request.assign': 'Назначить',
    'request.reassign': 'Переназначить',
    'request.executor': 'Исполнитель',
    'request.search': 'Поиск...',
    'request.allStatuses': 'Все статусы',
    'request.notFound': 'Заявок не найдено',
    'request.changeFilters': 'Попробуйте изменить фильтры',

    // Executors
    'executor.available': 'Доступно',
    'executor.busy': 'Заняты',
    'executor.offline': 'Оффлайн',
    'executor.all': 'Все исполнители',
    'executor.add': 'Добавить исполнителя',
    'executor.completed': 'выполнено',
    'executor.active': 'активных',

    // Residents
    'resident.add': 'Добавить жителя',
    'resident.total': 'всего',
    'resident.noResidents': 'Жителей пока нет',
    'resident.willAppear': 'Жители появятся после создания заявок',

    // Status
    'status.new': 'Новая',
    'status.assigned': 'Назначена',
    'status.accepted': 'Принята',
    'status.in_progress': 'В работе',
    'status.pending_approval': 'Ожидает подтверждения',
    'status.completed': 'Выполнена',
    'status.cancelled': 'Отменена',

    // Priority
    'priority.low': 'Низкий',
    'priority.medium': 'Средний',
    'priority.high': 'Высокий',
    'priority.urgent': 'Срочный',

    // Forms
    'form.name': 'ФИО',
    'form.phone': 'Телефон',
    'form.address': 'Адрес',
    'form.apartment': 'Квартира',
    'form.login': 'Логин',
    'form.password': 'Пароль',
    'form.specialization': 'Специализация',
    'form.generate': 'Сгенерировать',
    'form.cancel': 'Отмена',
    'form.add': 'Добавить',
    'form.save': 'Сохранить',
    'form.done': 'Готово',
    'form.copy': 'Копировать',
    'form.copied': 'Скопировано!',
    'form.fillAll': 'Заполните все поля',
    'form.credentials': 'Данные для входа',

    // Timer
    'timer.start': 'Начать работу',
    'timer.pause': 'Пауза',
    'timer.resume': 'Продолжить',
    'timer.finish': 'Завершить',
    'timer.elapsed': 'Прошло времени',

    // Settings
    'settings.general': 'Общие',
    'settings.notifications': 'Уведомления',
    'settings.integrations': 'Интеграции',
    'settings.users': 'Пользователи',
    'settings.language': 'Язык',

    // Common
    'common.all': 'Все',
    'common.delete': 'Удалить',
    'common.edit': 'Редактировать',
    'common.view': 'Просмотр',
    'common.refresh': 'Обновить',
    'common.loading': 'Загрузка...',
    'common.noData': 'Нет данных',

    // Rentals
    'rental.title': 'Аренда квартир',
    'rental.addApartment': 'Добавить квартиру',
    'rental.noApartments': 'Квартир пока нет',
    'rental.addFirst': 'Добавьте первую квартиру для управления арендой',
    'rental.occupied': 'Занята',
    'rental.available': 'Свободна',
    'rental.records': 'записей',
    'rental.currentGuest': 'Текущий гость',
    'rental.until': 'до',
    'rental.deleteApartment': 'Удалить квартиру',
    'rental.owner': 'Владелец',
    'rental.addRecord': 'Добавить запись',
    'rental.rentalHistory': 'История аренды',
    'rental.noRecords': 'Записей пока нет',
    'rental.active': 'Активно',
    'rental.apartmentName': 'Название',
    'rental.ownerData': 'Данные владельца (тенанта)',
    'rental.guestNames': 'Имена гостей',
    'rental.passportInfo': 'Паспортные данные',
    'rental.checkIn': 'Дата заезда',
    'rental.checkOut': 'Дата выезда',
    'rental.amount': 'Сумма',
    'rental.currency': 'Валюта',
    'rental.notes': 'Заметки (опционально)',
    'rental.apartmentAdded': 'Квартира добавлена!',
    'rental.ownerCredentials': 'Данные для входа владельца:',
    'rental.generateCredentials': 'Сгенерировать логин/пароль',
    'rental.confirmDelete': 'Подтвердите удаление',
    'rental.enterOwnerPassword': 'Введите пароль владельца квартиры для подтверждения удаления',
    'rental.wrongPassword': 'Неверный пароль',

    // Tenant Dashboard
    'tenant.myApartments': 'Мои квартиры',
    'tenant.welcome': 'Добро пожаловать',
    'tenant.apartments': 'Квартир',
    'tenant.records': 'Записей',
    'tenant.totalGuests': 'Гостей всего',
    'tenant.income': 'Доход (сум)',
    'tenant.noAssigned': 'Нет назначенных квартир',
    'tenant.contactManager': 'Обратитесь к менеджеру для добавления квартиры',
    'tenant.now': 'Сейчас',
    'tenant.completed': 'Завершено',
    'tenant.moreRecords': 'И ещё записей...',
    'tenant.guest': 'гость',
    'tenant.guests': 'гостя',
    'tenant.guestsMany': 'гостей',
    'tenant.checkInLabel': 'Заезд',
    'tenant.checkOutLabel': 'Выезд',

    // Announcements
    'announcements.title': 'Объявления',
    'announcements.forResidents': 'Для жителей',
    'announcements.forStaff': 'Для сотрудников',
    'announcements.add': 'Добавить объявление',
    'announcements.noAnnouncements': 'Объявлений пока нет',
    'announcements.addFirst': 'Добавьте первое объявление',
    'announcements.titleLabel': 'Заголовок',
    'announcements.content': 'Содержание',
    'announcements.type': 'Тип',
    'announcements.priority': 'Приоритет',
    'announcements.expiresAt': 'Срок действия (опционально)',
    'announcements.priorityNormal': 'Обычный',
    'announcements.priorityImportant': 'Важный',
    'announcements.priorityUrgent': 'Срочный',
    'announcements.views': 'просмотров',
    'announcements.author': 'Автор',
    'announcements.createdAt': 'Создано',
    'announcements.myAnnouncements': 'Мои объявления',
    'announcements.allAnnouncements': 'Все объявления',
    'announcements.noNew': 'Нет новых объявлений',
    'announcements.new': 'Новое',

    // Meetings - Собрания
    'meetings.title': 'Собрания жильцов',
    'meetings.subtitle': 'Организация и проведение собраний',
    'meetings.create': 'Созвать собрание',
    'meetings.noMeetings': 'Собраний пока нет',
    'meetings.createFirst': 'Создайте первое собрание',

    // Meeting statuses
    'meetings.status.draft': 'Черновик',
    'meetings.status.pending_moderation': 'На модерации',
    'meetings.status.schedule_poll_open': 'Выбор даты',
    'meetings.status.schedule_confirmed': 'Дата утверждена',
    'meetings.status.voting_open': 'Голосование',
    'meetings.status.voting_closed': 'Голосование закрыто',
    'meetings.status.results_published': 'Итоги опубликованы',
    'meetings.status.protocol_generated': 'Протокол готов',
    'meetings.status.protocol_approved': 'Протокол утвержден',
    'meetings.status.cancelled': 'Отменено',

    // Meeting format
    'meetings.format.online': 'Онлайн',
    'meetings.format.offline': 'Очное',
    'meetings.format.hybrid': 'Смешанное',

    // Meeting creation wizard
    'meetings.wizard.step1': 'Тип собрания',
    'meetings.wizard.step2': 'Повестка дня',
    'meetings.wizard.step3': 'Дата и время',
    'meetings.wizard.step4': 'Публикация',
    'meetings.wizard.organizerType': 'Организатор',
    'meetings.wizard.organizerResident': 'Житель (инициатива)',
    'meetings.wizard.organizerManagement': 'Управляющая компания',
    'meetings.wizard.selectFormat': 'Формат проведения',
    'meetings.wizard.selectAgenda': 'Выберите вопросы повестки',
    'meetings.wizard.addCustomQuestion': 'Добавить свой вопрос',
    'meetings.wizard.questionTitle': 'Заголовок вопроса',
    'meetings.wizard.questionDescription': 'Описание',
    'meetings.wizard.attachMaterials': 'Прикрепить материалы',
    'meetings.wizard.threshold': 'Порог принятия',
    'meetings.wizard.scheduleDatetime': 'Предложите 3 варианта даты и времени',
    'meetings.wizard.autoGenerate': 'Сгенерировать автоматически',
    'meetings.wizard.location': 'Место проведения (для очного/смешанного)',
    'meetings.wizard.publish': 'Опубликовать инициативу',
    'meetings.wizard.saveDraft': 'Сохранить черновик',

    // Schedule voting
    'meetings.schedule.title': 'Выберите удобную дату',
    'meetings.schedule.voteFor': 'Голосовать',
    'meetings.schedule.yourVote': 'Ваш голос',
    'meetings.schedule.votes': 'голосов',
    'meetings.schedule.endsAt': 'Голосование заканчивается',
    'meetings.schedule.confirmed': 'Дата подтверждена',

    // Agenda voting
    'meetings.voting.title': 'Голосование по вопросам',
    'meetings.voting.for': 'За',
    'meetings.voting.against': 'Против',
    'meetings.voting.abstain': 'Воздержаться',
    'meetings.voting.voted': 'Вы проголосовали',
    'meetings.voting.notVoted': 'Вы еще не голосовали',
    'meetings.voting.changeVote': 'Изменить голос',
    'meetings.voting.confirmVote': 'Подтвердить голос',
    'meetings.voting.verifyIdentity': 'Подтвердите личность',
    'meetings.voting.enterOTP': 'Введите код из SMS',
    'meetings.voting.sendOTP': 'Отправить код',
    'meetings.voting.resendOTP': 'Отправить повторно',
    'meetings.voting.otpSent': 'Код отправлен на',
    'meetings.voting.otpInvalid': 'Неверный код',
    'meetings.voting.otpExpired': 'Код истек, запросите новый',

    // Results
    'meetings.results.title': 'Результаты голосования',
    'meetings.results.approved': 'Принято',
    'meetings.results.rejected': 'Не принято',
    'meetings.results.quorum': 'Кворум',
    'meetings.results.quorumReached': 'Кворум достигнут',
    'meetings.results.quorumNotReached': 'Кворум не достигнут',
    'meetings.results.participation': 'Участие',
    'meetings.results.votesFor': 'За',
    'meetings.results.votesAgainst': 'Против',
    'meetings.results.votesAbstain': 'Воздержались',
    'meetings.results.threshold': 'Требуется',

    // Protocol
    'meetings.protocol.title': 'Протокол собрания',
    'meetings.protocol.generate': 'Сформировать протокол',
    'meetings.protocol.download': 'Скачать PDF',
    'meetings.protocol.sign': 'Подписать протокол',
    'meetings.protocol.signed': 'Протокол подписан',
    'meetings.protocol.signedBy': 'Подписано',

    // Actions
    'meetings.actions.approve': 'Одобрить',
    'meetings.actions.reject': 'Отклонить',
    'meetings.actions.openVoting': 'Открыть голосование',
    'meetings.actions.closeVoting': 'Закрыть голосование',
    'meetings.actions.publishResults': 'Опубликовать итоги',
    'meetings.actions.cancel': 'Отменить собрание',
    'meetings.actions.viewDetails': 'Подробнее',
    'meetings.actions.viewAgenda': 'Повестка дня',
    'meetings.actions.viewMaterials': 'Материалы',

    // Notifications
    'meetings.notify.created': 'Инициировано новое собрание',
    'meetings.notify.scheduleVote': 'Выберите удобную дату',
    'meetings.notify.scheduleConfirmed': 'Дата собрания подтверждена',
    'meetings.notify.votingOpen': 'Голосование открыто',
    'meetings.notify.votingReminder': 'Не забудьте проголосовать',
    'meetings.notify.resultsPublished': 'Итоги собрания опубликованы',
    'meetings.notify.protocolReady': 'Протокол собрания готов',

    // Settings
    'meetings.settings.title': 'Настройки собраний',
    'meetings.settings.votingUnit': 'Единица голоса',
    'meetings.settings.votingUnitApartment': '1 квартира = 1 голос',
    'meetings.settings.votingUnitOwner': '1 собственник = 1 голос',
    'meetings.settings.votingUnitShare': 'По долям собственности',
    'meetings.settings.quorum': 'Кворум (%)',
    'meetings.settings.allowResidentInitiative': 'Разрешить инициативу жильцов',
    'meetings.settings.requireModeration': 'Требовать модерацию УК',
    'meetings.settings.requireOTP': 'Подтверждение по SMS',
    'meetings.settings.showIntermediateResults': 'Показывать промежуточные результаты',

    // Evidence
    'meetings.evidence.title': 'Отчет доказательств',
    'meetings.evidence.download': 'Скачать отчет',
    'meetings.evidence.totalVotes': 'Всего голосов',
    'meetings.evidence.otpVerified': 'Подтверждено по SMS',
    'meetings.evidence.uniqueVoters': 'Уникальных голосующих',

    // Login Page
    'login.enterLogin': 'Введите логин',
    'login.enterPassword': 'Введите пароль',
    'login.mustAcceptTerms': 'Необходимо принять условия публичной оферты',
    'login.wrongCredentials': 'Неверный логин или пароль',
    'login.loginError': 'Ошибка при входе',
    'login.loading': 'Вход...',
    'login.agreeWith': 'Я согласен с условиями',
    'login.publicOffer': 'публичной оферты',
    'login.acceptFirst': 'Сначала примите условия публичной оферты',
    'login.offerTitle': 'Публичная оферта',
    'login.scrollToAccept': 'Прокрутите до конца документа, чтобы принять условия оферты',
    'login.documentRead': 'Вы прочитали документ',
    'login.scrollDown': 'Прокрутите вниз для прочтения',
    'login.close': 'Закрыть',
    'login.accept': 'Принять',

    // Roles
    'role.admin': 'Администратор',
    'role.manager': 'Менеджер',
    'role.department_head': 'Глава отдела',
    'role.executor': 'Исполнитель',
    'role.resident': 'Житель',
    'role.plumber': 'Сантехник',
    'role.electrician': 'Электрик',
    'role.security': 'Охрана',

    // Buildings / CRM
    'building.title': 'Здания',
    'building.add': 'Добавить здание',
    'building.edit': 'Редактировать здание',
    'building.delete': 'Удалить здание',
    'building.address': 'Адрес',
    'building.name': 'Название',
    'building.floors': 'Этажей',
    'building.entrances': 'Подъездов',
    'building.apartments': 'Квартир',
    'building.residents': 'Жителей',
    'building.noBuildings': 'Зданий пока нет',
    'building.addFirst': 'Добавьте первое здание',

    // Owners
    'owner.title': 'Собственники',
    'owner.add': 'Добавить собственника',
    'owner.edit': 'Редактировать',
    'owner.delete': 'Удалить',
    'owner.name': 'ФИО собственника',
    'owner.phone': 'Телефон',
    'owner.email': 'Email',
    'owner.share': 'Доля собственности',
    'owner.noOwners': 'Собственников пока нет',

    // Personal Accounts
    'account.title': 'Лицевые счета',
    'account.number': 'Номер счёта',
    'account.balance': 'Баланс',
    'account.debt': 'Задолженность',
    'account.lastPayment': 'Последний платёж',
    'account.noAccounts': 'Счетов пока нет',

    // Work Orders
    'workorder.title': 'Наряды на работы',
    'workorder.create': 'Создать наряд',
    'workorder.number': 'Номер наряда',
    'workorder.status': 'Статус',
    'workorder.executor': 'Исполнитель',
    'workorder.deadline': 'Срок выполнения',
    'workorder.noWorkOrders': 'Нарядов пока нет',

    // Activity Log
    'activity.title': 'Журнал действий',
    'activity.created': 'Создано',
    'activity.updated': 'Обновлено',
    'activity.deleted': 'Удалено',
    'activity.login': 'Вход в систему',
    'activity.logout': 'Выход из системы',
    'activity.noActivity': 'Нет записей',
    'activity.user': 'Пользователь',
    'activity.action': 'Действие',
    'activity.timestamp': 'Время',
    'activity.details': 'Детали',

    // Monitoring
    'monitoring.title': 'Мониторинг системы',
    'monitoring.health': 'Состояние системы',
    'monitoring.uptime': 'Время работы',
    'monitoring.requests': 'Запросов',
    'monitoring.errors': 'Ошибок',
    'monitoring.latency': 'Задержка',
    'monitoring.healthy': 'Работает',
    'monitoring.degraded': 'Замедление',
    'monitoring.down': 'Недоступно',

    // Executor Stats
    'stats.title': 'Статистика',
    'stats.completed': 'Выполнено',
    'stats.thisWeek': 'За неделю',
    'stats.thisMonth': 'За месяц',
    'stats.rating': 'Рейтинг',
    'stats.avgTime': 'Среднее время',
    'stats.noStats': 'Нет данных для отображения',

    // Schedule
    'schedule.title': 'Расписание',
    'schedule.today': 'Сегодня',
    'schedule.tomorrow': 'Завтра',
    'schedule.thisWeek': 'На этой неделе',
    'schedule.noSchedule': 'Нет запланированных работ',
    'schedule.addEvent': 'Добавить событие',

    // Chat
    'chat.title': 'Чат',
    'chat.support': 'Поддержка',
    'chat.newMessage': 'Новое сообщение',
    'chat.typeMessage': 'Введите сообщение...',
    'chat.send': 'Отправить',
    'chat.noMessages': 'Нет сообщений',
    'chat.startConversation': 'Начните общение',

    // Guest Access
    'guest.title': 'Гостевой доступ',
    'guest.createCode': 'Создать код',
    'guest.validUntil': 'Действует до',
    'guest.usages': 'Использований',
    'guest.active': 'Активен',
    'guest.expired': 'Истёк',
    'guest.used': 'Использован',
    'guest.noGuests': 'Нет гостевых кодов',
    'guest.generate': 'Сгенерировать QR-код',
    'guest.copyCode': 'Скопировать код',
    'guest.shareCode': 'Поделиться кодом',

    // QR Scanner
    'scanner.title': 'Сканер QR-кодов',
    'scanner.scan': 'Сканировать',
    'scanner.scanning': 'Сканирование...',
    'scanner.valid': 'Код действителен',
    'scanner.invalid': 'Код недействителен',
    'scanner.expired': 'Код истёк',
    'scanner.allow': 'Разрешить проход',
    'scanner.deny': 'Запретить проход',

    // Vehicles
    'vehicle.title': 'Транспорт',
    'vehicle.add': 'Добавить транспорт',
    'vehicle.plate': 'Номер',
    'vehicle.brand': 'Марка',
    'vehicle.model': 'Модель',
    'vehicle.color': 'Цвет',
    'vehicle.owner': 'Владелец',
    'vehicle.noVehicles': 'Транспорт не зарегистрирован',
    'vehicle.search': 'Поиск по номеру',

    // Contract
    'contract.title': 'Договор',
    'contract.number': 'Номер договора',
    'contract.status': 'Статус',
    'contract.signed': 'Подписан',
    'contract.pending': 'Ожидает подписания',
    'contract.startDate': 'Дата начала',
    'contract.endDate': 'Дата окончания',
    'contract.download': 'Скачать PDF',
    'contract.sign': 'Подписать',

    // Profile
    'profile.title': 'Мой профиль',
    'profile.personalData': 'Ваши личные данные',
    'profile.personalAccount': 'Лицевой счёт',
    'profile.loginHint': 'Логин для входа',
    'profile.cannotChange': 'Нельзя изменить',
    'profile.phone': 'Телефон',
    'profile.notSpecified': 'Не указан',
    'profile.password': 'Пароль',
    'profile.currentPassword': 'Текущий пароль',
    'profile.newPassword': 'Новый пароль',
    'profile.confirmPassword': 'Подтвердите пароль',
    'profile.passwordChanged': 'Пароль успешно изменён',
    'profile.phoneSaved': 'Телефон сохранён',

    // Errors
    'error.generic': 'Произошла ошибка',
    'error.network': 'Ошибка сети',
    'error.unauthorized': 'Необходима авторизация',
    'error.forbidden': 'Доступ запрещён',
    'error.notFound': 'Не найдено',
    'error.tryAgain': 'Попробуйте позже',

    // Confirmations
    'confirm.delete': 'Подтвердите удаление',
    'confirm.cancel': 'Отменить',
    'confirm.yes': 'Да',
    'confirm.no': 'Нет',
    'confirm.sure': 'Вы уверены?',

    // Time
    'time.today': 'Сегодня',
    'time.yesterday': 'Вчера',
    'time.daysAgo': 'дней назад',
    'time.hoursAgo': 'часов назад',
    'time.minutesAgo': 'минут назад',
    'time.justNow': 'Только что',
  },
  uz: {
    // App name
    'app.name': 'My Helper',
    'app.subtitle': 'CRM Tizimi',

    // Auth
    'auth.title': 'Tizimga kirish',
    'auth.login': 'Login',
    'auth.password': 'Parol',
    'auth.enter': 'Kirish',
    'auth.error': 'Login yoki parol noto\'g\'ri',
    'auth.logout': 'Chiqish',

    // Navigation
    'nav.dashboard': 'Boshqaruv paneli',
    'nav.requests': 'Arizalar',
    'nav.executors': 'Ijrochilar',
    'nav.residents': 'Aholi',
    'nav.buildings': 'Binolar',
    'nav.activity': 'Faoliyat jurnali',
    'nav.reports': 'Hisobotlar',
    'nav.settings': 'Sozlamalar',
    'nav.monitoring': 'Monitoring',
    'nav.myRequests': 'Mening arizalarim',
    'nav.services': 'Xizmatlar',
    'nav.profile': 'Profil',
    'nav.more': 'Ko\'proq',
    'nav.rentals': 'Kvartira ijarasi',
    'nav.myApartments': 'Mening kvartiram',
    'nav.accounts': 'Shaxsiy hisoblar',
    'nav.owners': 'Mulkdorlar',
    'nav.workOrders': 'Ish buyruqlari',
    'nav.trainings': 'Treninglar',

    // Dashboard
    'dashboard.manager': 'Menejer paneli',
    'dashboard.admin': 'Administrator paneli',
    'dashboard.newRequests': 'Yangi arizalar',
    'dashboard.inProgress': 'Jarayonda',
    'dashboard.pendingApproval': 'Tasdiqlash kutilmoqda',
    'dashboard.completedWeek': 'Hafta davomida bajarilgan',
    'dashboard.weeklyRequests': 'Haftalik arizalar',
    'dashboard.byCategory': 'Kategoriya bo\'yicha',
    'dashboard.recentRequests': 'So\'nggi arizalar',
    'dashboard.viewAll': 'Hammasi',

    // Requests
    'request.assign': 'Tayinlash',
    'request.reassign': 'Qayta tayinlash',
    'request.executor': 'Ijrochi',
    'request.search': 'Qidirish...',
    'request.allStatuses': 'Barcha holatlar',
    'request.notFound': 'Arizalar topilmadi',
    'request.changeFilters': 'Filtrlarni o\'zgartirib ko\'ring',

    // Executors
    'executor.available': 'Mavjud',
    'executor.busy': 'Band',
    'executor.offline': 'Oflayn',
    'executor.all': 'Barcha ijrochilar',
    'executor.add': 'Ijrochi qo\'shish',
    'executor.completed': 'bajarilgan',
    'executor.active': 'faol',

    // Residents
    'resident.add': 'Aholi qo\'shish',
    'resident.total': 'jami',
    'resident.noResidents': 'Aholi hali yo\'q',
    'resident.willAppear': 'Arizalar yaratilgandan keyin aholi paydo bo\'ladi',

    // Status
    'status.new': 'Yangi',
    'status.assigned': 'Tayinlangan',
    'status.accepted': 'Qabul qilingan',
    'status.in_progress': 'Jarayonda',
    'status.pending_approval': 'Tasdiqlash kutilmoqda',
    'status.completed': 'Bajarilgan',
    'status.cancelled': 'Bekor qilingan',

    // Priority
    'priority.low': 'Past',
    'priority.medium': 'O\'rta',
    'priority.high': 'Yuqori',
    'priority.urgent': 'Shoshilinch',

    // Forms
    'form.name': 'F.I.O.',
    'form.phone': 'Telefon',
    'form.address': 'Manzil',
    'form.apartment': 'Kvartira',
    'form.login': 'Login',
    'form.password': 'Parol',
    'form.specialization': 'Mutaxassislik',
    'form.generate': 'Yaratish',
    'form.cancel': 'Bekor qilish',
    'form.add': 'Qo\'shish',
    'form.save': 'Saqlash',
    'form.done': 'Tayyor',
    'form.copy': 'Nusxalash',
    'form.copied': 'Nusxalandi!',
    'form.fillAll': 'Barcha maydonlarni to\'ldiring',
    'form.credentials': 'Kirish ma\'lumotlari',

    // Timer
    'timer.start': 'Ishni boshlash',
    'timer.pause': 'Pauza',
    'timer.resume': 'Davom ettirish',
    'timer.finish': 'Tugatish',
    'timer.elapsed': 'O\'tgan vaqt',

    // Settings
    'settings.general': 'Umumiy',
    'settings.notifications': 'Bildirishnomalar',
    'settings.integrations': 'Integratsiyalar',
    'settings.users': 'Foydalanuvchilar',
    'settings.language': 'Til',

    // Common
    'common.all': 'Hammasi',
    'common.delete': 'O\'chirish',
    'common.edit': 'Tahrirlash',
    'common.view': 'Ko\'rish',
    'common.refresh': 'Yangilash',
    'common.loading': 'Yuklanmoqda...',
    'common.noData': 'Ma\'lumot yo\'q',

    // Rentals
    'rental.title': 'Kvartira ijarasi',
    'rental.addApartment': 'Kvartira qo\'shish',
    'rental.noApartments': 'Kvartiralar hali yo\'q',
    'rental.addFirst': 'Ijarani boshqarish uchun birinchi kvartirani qo\'shing',
    'rental.occupied': 'Band',
    'rental.available': 'Bo\'sh',
    'rental.records': 'yozuvlar',
    'rental.currentGuest': 'Hozirgi mehmon',
    'rental.until': 'gacha',
    'rental.deleteApartment': 'Kvartirani o\'chirish',
    'rental.owner': 'Egasi',
    'rental.addRecord': 'Yozuv qo\'shish',
    'rental.rentalHistory': 'Ijara tarixi',
    'rental.noRecords': 'Yozuvlar hali yo\'q',
    'rental.active': 'Faol',
    'rental.apartmentName': 'Nomi',
    'rental.ownerData': 'Egasi ma\'lumotlari (tenant)',
    'rental.guestNames': 'Mehmonlar ismi',
    'rental.passportInfo': 'Pasport ma\'lumotlari',
    'rental.checkIn': 'Kirish sanasi',
    'rental.checkOut': 'Chiqish sanasi',
    'rental.amount': 'Summa',
    'rental.currency': 'Valyuta',
    'rental.notes': 'Izohlar (ixtiyoriy)',
    'rental.apartmentAdded': 'Kvartira qo\'shildi!',
    'rental.ownerCredentials': 'Egasi kirish ma\'lumotlari:',
    'rental.generateCredentials': 'Login/parol yaratish',
    'rental.confirmDelete': 'O\'chirishni tasdiqlang',
    'rental.enterOwnerPassword': 'O\'chirishni tasdiqlash uchun kvartira egasining parolini kiriting',
    'rental.wrongPassword': 'Parol noto\'g\'ri',

    // Tenant Dashboard
    'tenant.myApartments': 'Mening kvartiram',
    'tenant.welcome': 'Xush kelibsiz',
    'tenant.apartments': 'Kvartiralar',
    'tenant.records': 'Yozuvlar',
    'tenant.totalGuests': 'Jami mehmonlar',
    'tenant.income': 'Daromad (so\'m)',
    'tenant.noAssigned': 'Tayinlangan kvartiralar yo\'q',
    'tenant.contactManager': 'Kvartira qo\'shish uchun menejer bilan bog\'laning',
    'tenant.now': 'Hozir',
    'tenant.completed': 'Tugallangan',
    'tenant.moreRecords': 'Va yana yozuvlar...',
    'tenant.guest': 'mehmon',
    'tenant.guests': 'mehmon',
    'tenant.guestsMany': 'mehmon',
    'tenant.checkInLabel': 'Kirish',
    'tenant.checkOutLabel': 'Chiqish',

    // Announcements
    'announcements.title': 'E\'lonlar',
    'announcements.forResidents': 'Aholiga',
    'announcements.forStaff': 'Xodimlarga',
    'announcements.add': 'E\'lon qo\'shish',
    'announcements.noAnnouncements': 'E\'lonlar hali yo\'q',
    'announcements.addFirst': 'Birinchi e\'lonni qo\'shing',
    'announcements.titleLabel': 'Sarlavha',
    'announcements.content': 'Mazmuni',
    'announcements.type': 'Turi',
    'announcements.priority': 'Muhimlik',
    'announcements.expiresAt': 'Amal qilish muddati (ixtiyoriy)',
    'announcements.priorityNormal': 'Oddiy',
    'announcements.priorityImportant': 'Muhim',
    'announcements.priorityUrgent': 'Shoshilinch',
    'announcements.views': 'ko\'rishlar',
    'announcements.author': 'Muallif',
    'announcements.createdAt': 'Yaratilgan',
    'announcements.myAnnouncements': 'Mening e\'lonlarim',
    'announcements.allAnnouncements': 'Barcha e\'lonlar',
    'announcements.noNew': 'Yangi e\'lonlar yo\'q',
    'announcements.new': 'Yangi',

    // Meetings - Yig'ilishlar
    'meetings.title': 'Aholining yig\'ilishlari',
    'meetings.subtitle': 'Yig\'ilishlarni tashkil etish va o\'tkazish',
    'meetings.create': 'Yig\'ilish chaqirish',
    'meetings.noMeetings': 'Yig\'ilishlar hali yo\'q',
    'meetings.createFirst': 'Birinchi yig\'ilishni yarating',

    // Meeting statuses
    'meetings.status.draft': 'Qoralama',
    'meetings.status.pending_moderation': 'Moderatsiyada',
    'meetings.status.schedule_poll_open': 'Sana tanlash',
    'meetings.status.schedule_confirmed': 'Sana tasdiqlangan',
    'meetings.status.voting_open': 'Ovoz berish',
    'meetings.status.voting_closed': 'Ovoz berish yopildi',
    'meetings.status.results_published': 'Natijalar e\'lon qilindi',
    'meetings.status.protocol_generated': 'Bayonnoma tayyor',
    'meetings.status.protocol_approved': 'Bayonnoma tasdiqlangan',
    'meetings.status.cancelled': 'Bekor qilindi',

    // Meeting format
    'meetings.format.online': 'Onlayn',
    'meetings.format.offline': 'Yuzma-yuz',
    'meetings.format.hybrid': 'Aralash',

    // Meeting creation wizard
    'meetings.wizard.step1': 'Yig\'ilish turi',
    'meetings.wizard.step2': 'Kun tartibi',
    'meetings.wizard.step3': 'Sana va vaqt',
    'meetings.wizard.step4': 'Nashr',
    'meetings.wizard.organizerType': 'Tashkilotchi',
    'meetings.wizard.organizerResident': 'Aholi (tashabbusi)',
    'meetings.wizard.organizerManagement': 'Boshqaruv kompaniyasi',
    'meetings.wizard.selectFormat': 'O\'tkazish formati',
    'meetings.wizard.selectAgenda': 'Kun tartibi savollarini tanlang',
    'meetings.wizard.addCustomQuestion': 'O\'z savolingizni qo\'shing',
    'meetings.wizard.questionTitle': 'Savol sarlavhasi',
    'meetings.wizard.questionDescription': 'Tavsif',
    'meetings.wizard.attachMaterials': 'Materiallarni biriktiring',
    'meetings.wizard.threshold': 'Qabul qilish chegarasi',
    'meetings.wizard.scheduleDatetime': 'Sana va vaqtning 3 variantini taklif qiling',
    'meetings.wizard.autoGenerate': 'Avtomatik yaratish',
    'meetings.wizard.location': 'O\'tkazish joyi (yuzma-yuz/aralash uchun)',
    'meetings.wizard.publish': 'Tashabbusni e\'lon qilish',
    'meetings.wizard.saveDraft': 'Qoralama saqlash',

    // Schedule voting
    'meetings.schedule.title': 'Qulay sanani tanlang',
    'meetings.schedule.voteFor': 'Ovoz berish',
    'meetings.schedule.yourVote': 'Sizning ovozingiz',
    'meetings.schedule.votes': 'ovoz',
    'meetings.schedule.endsAt': 'Ovoz berish tugaydi',
    'meetings.schedule.confirmed': 'Sana tasdiqlangan',

    // Agenda voting
    'meetings.voting.title': 'Savollarga ovoz berish',
    'meetings.voting.for': 'Yoqlab',
    'meetings.voting.against': 'Qarshi',
    'meetings.voting.abstain': 'Betaraf',
    'meetings.voting.voted': 'Siz ovoz berdingiz',
    'meetings.voting.notVoted': 'Siz hali ovoz bermadingiz',
    'meetings.voting.changeVote': 'Ovozni o\'zgartirish',
    'meetings.voting.confirmVote': 'Ovozni tasdiqlash',
    'meetings.voting.verifyIdentity': 'Shaxsingizni tasdiqlang',
    'meetings.voting.enterOTP': 'SMS kodini kiriting',
    'meetings.voting.sendOTP': 'Kod yuborish',
    'meetings.voting.resendOTP': 'Qayta yuborish',
    'meetings.voting.otpSent': 'Kod yuborildi',
    'meetings.voting.otpInvalid': 'Kod noto\'g\'ri',
    'meetings.voting.otpExpired': 'Kod muddati tugadi, yangi so\'rang',

    // Results
    'meetings.results.title': 'Ovoz berish natijalari',
    'meetings.results.approved': 'Qabul qilindi',
    'meetings.results.rejected': 'Qabul qilinmadi',
    'meetings.results.quorum': 'Kvorum',
    'meetings.results.quorumReached': 'Kvorum yetdi',
    'meetings.results.quorumNotReached': 'Kvorum yetmadi',
    'meetings.results.participation': 'Ishtirok',
    'meetings.results.votesFor': 'Yoqlab',
    'meetings.results.votesAgainst': 'Qarshi',
    'meetings.results.votesAbstain': 'Betaraf',
    'meetings.results.threshold': 'Kerak',

    // Protocol
    'meetings.protocol.title': 'Yig\'ilish bayonnomasi',
    'meetings.protocol.generate': 'Bayonnoma yaratish',
    'meetings.protocol.download': 'PDF yuklash',
    'meetings.protocol.sign': 'Bayonnomani imzolash',
    'meetings.protocol.signed': 'Bayonnoma imzolangan',
    'meetings.protocol.signedBy': 'Imzolagan',

    // Actions
    'meetings.actions.approve': 'Tasdiqlash',
    'meetings.actions.reject': 'Rad etish',
    'meetings.actions.openVoting': 'Ovoz berishni ochish',
    'meetings.actions.closeVoting': 'Ovoz berishni yopish',
    'meetings.actions.publishResults': 'Natijalarni e\'lon qilish',
    'meetings.actions.cancel': 'Yig\'ilishni bekor qilish',
    'meetings.actions.viewDetails': 'Batafsil',
    'meetings.actions.viewAgenda': 'Kun tartibi',
    'meetings.actions.viewMaterials': 'Materiallar',

    // Notifications
    'meetings.notify.created': 'Yangi yig\'ilish boshlandi',
    'meetings.notify.scheduleVote': 'Qulay sanani tanlang',
    'meetings.notify.scheduleConfirmed': 'Yig\'ilish sanasi tasdiqlandi',
    'meetings.notify.votingOpen': 'Ovoz berish ochildi',
    'meetings.notify.votingReminder': 'Ovoz berishni unutmang',
    'meetings.notify.resultsPublished': 'Yig\'ilish natijalari e\'lon qilindi',
    'meetings.notify.protocolReady': 'Yig\'ilish bayonnomasi tayyor',

    // Settings
    'meetings.settings.title': 'Yig\'ilish sozlamalari',
    'meetings.settings.votingUnit': 'Ovoz birligi',
    'meetings.settings.votingUnitApartment': '1 kvartira = 1 ovoz',
    'meetings.settings.votingUnitOwner': '1 egasi = 1 ovoz',
    'meetings.settings.votingUnitShare': 'Ulush bo\'yicha',
    'meetings.settings.quorum': 'Kvorum (%)',
    'meetings.settings.allowResidentInitiative': 'Aholi tashabbusiga ruxsat',
    'meetings.settings.requireModeration': 'UK moderatsiyasi kerak',
    'meetings.settings.requireOTP': 'SMS orqali tasdiqlash',
    'meetings.settings.showIntermediateResults': 'Oraliq natijalarni ko\'rsatish',

    // Evidence
    'meetings.evidence.title': 'Dalillar hisoboti',
    'meetings.evidence.download': 'Hisobotni yuklash',
    'meetings.evidence.totalVotes': 'Jami ovozlar',
    'meetings.evidence.otpVerified': 'SMS orqali tasdiqlangan',
    'meetings.evidence.uniqueVoters': 'Noyob ovoz beruvchilar',

    // Login Page
    'login.enterLogin': 'Login kiriting',
    'login.enterPassword': 'Parol kiriting',
    'login.mustAcceptTerms': 'Ommaviy oferta shartlarini qabul qilish kerak',
    'login.wrongCredentials': 'Login yoki parol noto\'g\'ri',
    'login.loginError': 'Kirishda xatolik',
    'login.loading': 'Kirish...',
    'login.agreeWith': 'Men shartlarga roziman',
    'login.publicOffer': 'ommaviy oferta',
    'login.acceptFirst': 'Avval ommaviy oferta shartlarini qabul qiling',
    'login.offerTitle': 'Ommaviy oferta',
    'login.scrollToAccept': 'Oferta shartlarini qabul qilish uchun hujjat oxirigacha aylantiring',
    'login.documentRead': 'Siz hujjatni o\'qidingiz',
    'login.scrollDown': 'O\'qish uchun pastga aylantiring',
    'login.close': 'Yopish',
    'login.accept': 'Qabul qilish',

    // Roles
    'role.admin': 'Administrator',
    'role.manager': 'Menejer',
    'role.department_head': 'Bo\'lim boshlig\'i',
    'role.executor': 'Ijrochi',
    'role.resident': 'Aholi',
    'role.plumber': 'Santexnik',
    'role.electrician': 'Elektrik',
    'role.security': 'Qorovul',

    // Buildings / CRM
    'building.title': 'Binolar',
    'building.add': 'Bino qo\'shish',
    'building.edit': 'Binoni tahrirlash',
    'building.delete': 'Binoni o\'chirish',
    'building.address': 'Manzil',
    'building.name': 'Nomi',
    'building.floors': 'Qavatlar',
    'building.entrances': 'Kirishlar',
    'building.apartments': 'Kvartiralar',
    'building.residents': 'Aholisi',
    'building.noBuildings': 'Binolar hali yo\'q',
    'building.addFirst': 'Birinchi binoni qo\'shing',

    // Owners
    'owner.title': 'Mulkdorlar',
    'owner.add': 'Mulkdor qo\'shish',
    'owner.edit': 'Tahrirlash',
    'owner.delete': 'O\'chirish',
    'owner.name': 'Mulkdor ismi',
    'owner.phone': 'Telefon',
    'owner.email': 'Email',
    'owner.share': 'Mulk ulushi',
    'owner.noOwners': 'Mulkdorlar hali yo\'q',

    // Personal Accounts
    'account.title': 'Shaxsiy hisoblar',
    'account.number': 'Hisob raqami',
    'account.balance': 'Balans',
    'account.debt': 'Qarz',
    'account.lastPayment': 'Oxirgi to\'lov',
    'account.noAccounts': 'Hisoblar hali yo\'q',

    // Work Orders
    'workorder.title': 'Ish buyruqlari',
    'workorder.create': 'Buyruq yaratish',
    'workorder.number': 'Buyruq raqami',
    'workorder.status': 'Holat',
    'workorder.executor': 'Ijrochi',
    'workorder.deadline': 'Muddat',
    'workorder.noWorkOrders': 'Buyruqlar hali yo\'q',

    // Activity Log
    'activity.title': 'Faoliyat jurnali',
    'activity.created': 'Yaratildi',
    'activity.updated': 'Yangilandi',
    'activity.deleted': 'O\'chirildi',
    'activity.login': 'Tizimga kirish',
    'activity.logout': 'Tizimdan chiqish',
    'activity.noActivity': 'Yozuvlar yo\'q',
    'activity.user': 'Foydalanuvchi',
    'activity.action': 'Harakat',
    'activity.timestamp': 'Vaqt',
    'activity.details': 'Tafsilotlar',

    // Monitoring
    'monitoring.title': 'Tizim monitoringi',
    'monitoring.health': 'Tizim holati',
    'monitoring.uptime': 'Ishlash vaqti',
    'monitoring.requests': 'So\'rovlar',
    'monitoring.errors': 'Xatolar',
    'monitoring.latency': 'Kechikish',
    'monitoring.healthy': 'Ishlayapti',
    'monitoring.degraded': 'Sekinlashish',
    'monitoring.down': 'Mavjud emas',

    // Executor Stats
    'stats.title': 'Statistika',
    'stats.completed': 'Bajarilgan',
    'stats.thisWeek': 'Bu hafta',
    'stats.thisMonth': 'Bu oy',
    'stats.rating': 'Reyting',
    'stats.avgTime': 'O\'rtacha vaqt',
    'stats.noStats': 'Ko\'rsatish uchun ma\'lumot yo\'q',

    // Schedule
    'schedule.title': 'Jadval',
    'schedule.today': 'Bugun',
    'schedule.tomorrow': 'Ertaga',
    'schedule.thisWeek': 'Bu hafta',
    'schedule.noSchedule': 'Rejalashtirilgan ishlar yo\'q',
    'schedule.addEvent': 'Voqea qo\'shish',

    // Chat
    'chat.title': 'Chat',
    'chat.support': 'Yordam',
    'chat.newMessage': 'Yangi xabar',
    'chat.typeMessage': 'Xabar yozing...',
    'chat.send': 'Yuborish',
    'chat.noMessages': 'Xabarlar yo\'q',
    'chat.startConversation': 'Suhbatni boshlang',

    // Guest Access
    'guest.title': 'Mehmon kirishi',
    'guest.createCode': 'Kod yaratish',
    'guest.validUntil': 'Amal qiladi',
    'guest.usages': 'Foydalanishlar',
    'guest.active': 'Faol',
    'guest.expired': 'Muddati tugagan',
    'guest.used': 'Ishlatilgan',
    'guest.noGuests': 'Mehmon kodlari yo\'q',
    'guest.generate': 'QR-kod yaratish',
    'guest.copyCode': 'Kodni nusxalash',
    'guest.shareCode': 'Kodni ulashish',

    // QR Scanner
    'scanner.title': 'QR-kod skaneri',
    'scanner.scan': 'Skanerlash',
    'scanner.scanning': 'Skanerlanmoqda...',
    'scanner.valid': 'Kod to\'g\'ri',
    'scanner.invalid': 'Kod noto\'g\'ri',
    'scanner.expired': 'Kod muddati tugagan',
    'scanner.allow': 'O\'tishga ruxsat',
    'scanner.deny': 'O\'tishni taqiqlash',

    // Vehicles
    'vehicle.title': 'Transport',
    'vehicle.add': 'Transport qo\'shish',
    'vehicle.plate': 'Raqam',
    'vehicle.brand': 'Marka',
    'vehicle.model': 'Model',
    'vehicle.color': 'Rang',
    'vehicle.owner': 'Egasi',
    'vehicle.noVehicles': 'Transport ro\'yxatdan o\'tmagan',
    'vehicle.search': 'Raqam bo\'yicha qidirish',

    // Contract
    'contract.title': 'Shartnoma',
    'contract.number': 'Shartnoma raqami',
    'contract.status': 'Holat',
    'contract.signed': 'Imzolangan',
    'contract.pending': 'Imzo kutilmoqda',
    'contract.startDate': 'Boshlanish sanasi',
    'contract.endDate': 'Tugash sanasi',
    'contract.download': 'PDF yuklash',
    'contract.sign': 'Imzolash',

    // Profile
    'profile.title': 'Mening profilim',
    'profile.personalData': 'Shaxsiy ma\'lumotlaringiz',
    'profile.personalAccount': 'Shaxsiy hisob',
    'profile.loginHint': 'Kirish logini',
    'profile.cannotChange': 'O\'zgartirib bo\'lmaydi',
    'profile.phone': 'Telefon',
    'profile.notSpecified': 'Ko\'rsatilmagan',
    'profile.password': 'Parol',
    'profile.currentPassword': 'Joriy parol',
    'profile.newPassword': 'Yangi parol',
    'profile.confirmPassword': 'Parolni tasdiqlang',
    'profile.passwordChanged': 'Parol muvaffaqiyatli o\'zgartirildi',
    'profile.phoneSaved': 'Telefon saqlandi',

    // Errors
    'error.generic': 'Xatolik yuz berdi',
    'error.network': 'Tarmoq xatosi',
    'error.unauthorized': 'Avtorizatsiya talab qilinadi',
    'error.forbidden': 'Kirish taqiqlangan',
    'error.notFound': 'Topilmadi',
    'error.tryAgain': 'Keyinroq urinib ko\'ring',

    // Confirmations
    'confirm.delete': 'O\'chirishni tasdiqlang',
    'confirm.cancel': 'Bekor qilish',
    'confirm.yes': 'Ha',
    'confirm.no': 'Yo\'q',
    'confirm.sure': 'Ishonchingiz komilmi?',

    // Time
    'time.today': 'Bugun',
    'time.yesterday': 'Kecha',
    'time.daysAgo': 'kun oldin',
    'time.hoursAgo': 'soat oldin',
    'time.minutesAgo': 'daqiqa oldin',
    'time.justNow': 'Hozirgina',
  }
};

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set, get) => ({
      language: 'ru',
      setLanguage: (lang) => set({ language: lang }),
      t: (key) => {
        const { language } = get();
        return translations[language][key] || key;
      },
    }),
    {
      name: 'language-storage',
    }
  )
);
